import { injectable, interfaces } from 'inversify';
import { action, computed, IObservableArray, makeObservable, observable, ObservableMap } from 'mobx';
import { getAcronym } from '../../helpers/string.helper';
import { IPagingInfoWithOrder, PaginationInfoDefault } from '../../models/paginationInfo.model';
import { ProjectRole } from '../../models/userRole.model';

export enum UserRole {
  User = 'User',
  Administrator = 'Administrator',
}

export enum Plan {
  Free = 'Free',
  Annotation = 'Annotation',
  Training = 'Training',
  Admin = 'Admin',
  Inactive = 'Inactive',
}

interface IUserInfo {
  id: string;
  email: string;
  fullName: string;
  company: string;
  country: string;
  phoneNumber: string;
  role: UserRole;
  showCart: boolean;
  projectRoles: IProjectRole[];
  emailChangePending: boolean;
}

export interface IAdminUserInfo {
  id: string;
  email: string;
  name: string;
  organization: string;
  country: string;
  registrationDate: string;
  lastLoginDate: string;
  workTime: string;
  role: UserRole;
  plan: Plan;
  customLimits: boolean;
  availableSpace?: number;
  workspaces?: number;
  workspaceUsers?: number;
  isActive: boolean;
  isEmailConfirmed: boolean;
  imagesSize: number;
  attachmentSizeInB: number;
  dataSizeInMb: number;
  planExpirationDate: string | null;
  dataRetentionDate: string | null;
}

export interface IPlanInfo {
  plan: Plan;
  availableSpace?: number;
  workspaces?: number;
  workspaceUsers?: number;
  customLimits: boolean;
  isActive: boolean;
  planExpirationDate: string | null;
  dataRetentionDate: string | null;
}

export namespace IPlanInfo {
  export const Empty: IPlanInfo = {
    plan: Plan.Free,
    customLimits: false,
    isActive: false,
    dataRetentionDate: null,
    planExpirationDate: null,
  };
}

export interface IProjectRole {
  projectId: string;
  role: ProjectRole;
}

export enum ListViewMode {
  List = 'List',
  Tiles = 'Tiles',
}

export interface IUserStore {
  userInfo: IUserInfo;
  planInfo: IPlanInfo;
  adminUserInfo: IObservableArray<IAdminUserInfo>;
  adminUsersPaging: IPagingInfoWithOrder;
  plansInfo: IObservableArray<IPlanInfo>;
  planDefaults: IObservableArray<IPlanInfo>;
  plansPaging: IPagingInfoWithOrder;
  shortName: string;
  projectImageListViewMode: ListViewMode;
  datasetImageListViewMode: ListViewMode;
  attachmentsListViewMode: ListViewMode;
  projectListViewMode: ListViewMode;
  selectedAttributesPerProject: Readonly<Map<string, string[]>>;
  attributesNamesVisibilityPerProject: Readonly<Map<string, boolean>>;
  selectedAttributesPerProjectSerialized: string;
  attributesNamesVisibilitySerialized: string;
  opacityLevel: number;
  isImprovedVisibilityCursorEnabled: boolean;
  areCursorGuidesEnabled: boolean;
  setSelectedAttributesPerProject(projectId: string, selected: string[]): void;
  setAttributesNamesVisibility(projectId: string, visible: boolean): void;
}

export namespace IUserStore {
  export const $: interfaces.ServiceIdentifier<IUserStore> = Symbol('USER_STORE');
}

@injectable()
export class UserStore implements IUserStore {
  userInfo: IUserInfo = {
    id: '',
    email: '',
    fullName: '',
    company: '',
    country: '',
    phoneNumber: '',
    emailChangePending: false,
    showCart: false,
    role: UserRole.User,
    projectRoles: [],
  };

  planInfo: IPlanInfo = {
    customLimits: false,
    isActive: false,
    plan: Plan.Free,
    availableSpace: 0,
    workspaces: 0,
    workspaceUsers: 0,
    dataRetentionDate: null,
    planExpirationDate: null,
  };

  adminUserInfo: IObservableArray<IAdminUserInfo> = observable.array([]);

  plansInfo: IObservableArray<IPlanInfo> = observable.array([]);

  planDefaults: IObservableArray<IPlanInfo> = observable.array([]);

  adminUsersPaging: IPagingInfoWithOrder = {
    ...PaginationInfoDefault,
    orderBy: '',
    orderType: '',
    search: '',
  };

  plansPaging: IPagingInfoWithOrder = {
    pageNumber: 1,
    pagesCount: 1,
    totalCount: 1,
    pageSize: 100,
    orderBy: '',
    orderType: '',
  };

  projectImageListViewMode: ListViewMode = ListViewMode.Tiles;

  datasetImageListViewMode: ListViewMode = ListViewMode.Tiles;

  attachmentsListViewMode: ListViewMode = ListViewMode.List;

  projectListViewMode: ListViewMode = ListViewMode.List;

  selectedAttributesPerProject: ObservableMap<string, string[]> = new ObservableMap<string, string[]>();

  attributesNamesVisibilityPerProject: ObservableMap<string, boolean> = new ObservableMap<string, boolean>();

  opacityLevel: number = 20;

  isImprovedVisibilityCursorEnabled = false;

  areCursorGuidesEnabled = false;

  constructor() {
    makeObservable(this, {
      userInfo: observable,
      planInfo: observable,
      adminUserInfo: observable,
      plansInfo: observable,
      planDefaults: observable,
      adminUsersPaging: observable,
      plansPaging: observable,
      projectImageListViewMode: observable,
      datasetImageListViewMode: observable,
      attachmentsListViewMode: observable,
      projectListViewMode: observable,
      selectedAttributesPerProject: observable,
      attributesNamesVisibilityPerProject: observable,
      opacityLevel: observable,
      isImprovedVisibilityCursorEnabled: observable,
      areCursorGuidesEnabled: observable,
      shortName: computed,
      selectedAttributesPerProjectSerialized: computed,
      attributesNamesVisibilitySerialized: computed,
      setSelectedAttributesPerProject: action,
    });
  }

  get shortName(): string {
    const fullNameAcronym = getAcronym(this.userInfo.fullName);
    return (fullNameAcronym.length > 1 ? fullNameAcronym : this.userInfo.email).substring(0, 2);
  }

  get selectedAttributesPerProjectSerialized(): string {
    return JSON.stringify(this.selectedAttributesPerProject);
  }

  get attributesNamesVisibilitySerialized(): string {
    return JSON.stringify(this.attributesNamesVisibilityPerProject);
  }

  setSelectedAttributesPerProject = (projectId: string, selected: string[]): void => {
    this.selectedAttributesPerProject.set(projectId, selected);
  };

  setAttributesNamesVisibility(projectId: string, visible: boolean): void {
    this.attributesNamesVisibilityPerProject.set(projectId, visible);
  }
}
